import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Search, AlertCircle } from 'lucide-react';
import { useLocalAuth } from '../../hooks/useLocalAuth';
import { Building } from '../../types/building';
import { BuildingCard } from './BuildingCard';
import { ProfileMenu } from './ProfileMenu';
import { useLocalBuildings } from '../../hooks/useLocalBuildings';
import { ArchiveConfirmDialog } from './ArchiveConfirmDialog';
import { PDFImportDialog } from '../PDFImportDialog';
import { TechnicianList } from './TechnicianList';
import { KonzernIdManager } from './KonzernIdManager';
import { DeleteConfirmDialog } from './DeleteConfirmDialog';
import { ApiKeyManager } from '../ApiKeyManager';
import { WebhookManager } from '../WebhookManager';
import { ApiDocumentation } from '../ApiDocumentation';
import { SecurityComplianceMenu } from '../SecurityComplianceMenu';
import { MigrationDialog } from '../MigrationDialog';
import { NavigationBar } from '../navigation/NavigationBar';

export const Dashboard: React.FC = () => {
  const { user } = useLocalAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [buildingToArchive, setBuildingToArchive] = useState<string | null>(null);
  const [buildingToDelete, setBuildingToDelete] = useState<string | null>(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showTechnicianList, setShowTechnicianList] = useState(false);
  const [showKonzernIdManager, setShowKonzernIdManager] = useState(false);
  const [showApiKeyManager, setShowApiKeyManager] = useState(false);
  const [showWebhookManager, setShowWebhookManager] = useState(false);
  const [showApiDocumentation, setShowApiDocumentation] = useState(false);
  const [showApiMenu, setShowApiMenu] = useState(false);
  const [showSecurityMenu, setShowSecurityMenu] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  
  const { 
    buildings, 
    loading, 
    error, 
    moveToArchive, 
    restoreFromArchive, 
    removeBuilding,
    importBuilding,
    refreshBuildings
  } = useLocalBuildings(showArchived);
  
  const navigate = useNavigate();

  // Check if migration is needed
  useEffect(() => {
    const migrationComplete = localStorage.getItem('indexeddb_migration_complete');
    if (!migrationComplete && Object.keys(localStorage).some(key => key.startsWith('installtrack_'))) {
      setShowMigrationDialog(true);
    }
  }, []);

  const handleCreateBuilding = () => {
    navigate('/building/new');
  };

  const handleBuildingClick = (buildingId: string) => {
    navigate(`/building/${buildingId}`);
  };

  const handleArchiveBuilding = (id: string) => {
    setBuildingToArchive(id);
  };

  const confirmArchive = async () => {
    if (buildingToArchive) {
      await moveToArchive(buildingToArchive);
      setBuildingToArchive(null);
    }
  };

  const handleDeleteBuilding = (id: string) => {
    setBuildingToDelete(id);
  };

  const confirmDelete = async () => {
    if (buildingToDelete) {
      await removeBuilding(buildingToDelete);
      setBuildingToDelete(null);
    }
  };

  const handleRestoreBuilding = async (id: string) => {
    if (window.confirm('Möchten Sie dieses Gebäude aus dem Archiv wiederherstellen?')) {
      await restoreFromArchive(id);
    }
  };

  const handleImportBuilding = async (building: Building) => {
    const importedBuilding = await importBuilding(building);
    if (importedBuilding) {
      await refreshBuildings();
      setShowImportDialog(false);
    }
  };

  const handleMigrationComplete = () => {
    setShowMigrationDialog(false);
    window.location.reload(); // Reload to use the new storage
  };

  const filteredBuildings = buildings.filter(building => 
    building.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    building.address.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <NavigationBar
        showArchived={showArchived}
        onToggleArchive={() => setShowArchived(!showArchived)}
        onShowTechnicianList={() => setShowTechnicianList(true)}
        onShowKonzernIdManager={() => setShowKonzernIdManager(true)}
        onShowApiMenu={() => setShowApiMenu(!showApiMenu)}
        onShowWebhookManager={() => setShowWebhookManager(true)}
        onShowApiDocumentation={() => setShowApiDocumentation(true)}
        onShowSecurityMenu={() => setShowSecurityMenu(true)}
        onShowMigrationDialog={() => setShowMigrationDialog(true)}
        onShowImportDialog={() => setShowImportDialog(true)}
        onCreateBuilding={handleCreateBuilding}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search field */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Gebäude suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Buildings Grid */}
        {error && (
          <div className="mb-6 rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Gebäude werden geladen...</p>
          </div>
        ) : filteredBuildings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBuildings.map(building => (
              <BuildingCard
                key={building.id}
                building={building}
                onClick={() => handleBuildingClick(building.id)}
                onArchive={!showArchived && (!user?.permissions || user.permissions.canEditBuildings) ? () => handleArchiveBuilding(building.id) : undefined}
                onRestore={showArchived && (!user?.permissions || user.permissions.canEditBuildings) ? () => handleRestoreBuilding(building.id) : undefined}
                onDelete={() => handleDeleteBuilding(building.id)}
                isArchived={showArchived}
                canDelete={!user?.permissions || user.permissions.canDeleteBuildings}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-12 text-center">
            <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            {searchTerm ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Keine Ergebnisse gefunden
                </h3>
                <p className="text-gray-500 mb-4">
                  Es wurden keine Gebäude gefunden, die "{searchTerm}" enthalten.
                </p>
              </>
            ) : showArchived ? (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Keine archivierten Gebäude vorhanden
                </h3>
                <p className="text-gray-500 mb-4">
                  Archivierte Gebäude werden hier angezeigt, wenn Sie Projekte abschließen.
                </p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Keine Gebäude vorhanden
                </h3>
                <p className="text-gray-500 mb-4">
                  Erstellen Sie Ihr erstes Gebäude, um mit der Installation zu beginnen.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  {(!user?.permissions || user.permissions.canImportData) && (
                    <button
                      onClick={() => setShowImportDialog(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                      <span>PDF importieren</span>
                    </button>
                  )}
                  
                  {(!user?.permissions || user.permissions.canCreateBuildings) && (
                    <button
                      onClick={handleCreateBuilding}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-5 w-5" />
                      <span>Neues Gebäude erstellen</span>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </main>

      {/* Dialogs */}
      {buildingToArchive && (
        <ArchiveConfirmDialog
          onConfirm={confirmArchive}
          onCancel={() => setBuildingToArchive(null)}
        />
      )}
      
      {buildingToDelete && (
        <DeleteConfirmDialog
          onConfirm={confirmDelete}
          onCancel={() => setBuildingToDelete(null)}
          isArchived={showArchived}
        />
      )}
      
      {showImportDialog && (
        <PDFImportDialog
          onClose={() => setShowImportDialog(false)}
          onImport={handleImportBuilding}
        />
      )}

      {showTechnicianList && (
        <TechnicianList
          onClose={() => setShowTechnicianList(false)}
        />
      )}

      {showKonzernIdManager && (
        <KonzernIdManager
          onClose={() => setShowKonzernIdManager(false)}
        />
      )}

      {showApiKeyManager && (
        <ApiKeyManager
          onClose={() => setShowApiKeyManager(false)}
        />
      )}

      {showWebhookManager && (
        <WebhookManager
          onClose={() => setShowWebhookManager(false)}
        />
      )}

      {showApiDocumentation && (
        <ApiDocumentation
          onClose={() => setShowApiDocumentation(false)}
        />
      )}

      {showSecurityMenu && (
        <SecurityComplianceMenu
          onClose={() => setShowSecurityMenu(false)}
        />
      )}

      {showMigrationDialog && (
        <MigrationDialog
          onClose={() => setShowMigrationDialog(false)}
          onComplete={handleMigrationComplete}
        />
      )}
    </div>
  );
};